/**
 * CORE-1618 (прод 06.10.2026, комната 488) — выбор микрофона и камеры с экрана
 * входа доезжает до комнаты на вебе.
 *
 * Экран входа сохранял выбор в `LiveDevicePrefs`, а комната читала оттуда только
 * устройства (какой микрофон, какая камера), но не «включено / выключено»: веб
 * всегда входил с выключенными. Ведущий видел работающий предпросмотр, входил,
 * и начинал эфир без звука и картинки. Натив этот выбор применял с самого
 * начала — параметрами маршрута (`useLiveRoom`, `preflightDevices.ts`).
 *
 * Здесь выбор едет в памяти, а не в адресе и не в хранилище, и это намеренно:
 *   • в адресе он пережил бы перезагрузку, и микрофон, выключенный человеком
 *     уже в комнате, включился бы снова сам;
 *   • в хранилище он лежит от прошлого визита — вход в комнату мимо экрана
 *     входа (прямая ссылка, перезагрузка) применил бы чужой вчерашний выбор.
 * Выбор живёт от нажатия «Войти» до первого применения в ТОЙ ЖЕ комнате.
 */

export interface EntryDeviceChoice {
    /** Код комнаты, для которой выбор сделан. */
    roomCode: string;
    micOn: boolean;
    camOn: boolean;
}

let pending: EntryDeviceChoice | null = null;

export function stashEntryDeviceChoice(choice: EntryDeviceChoice): void {
    pending = choice;
}

/** Выбор для этой комнаты, если он ещё не применён. Не снимает его: экран
 *  комнаты монтируется дважды (Suspense), и снятый первым монтированием выбор
 *  второму бы не достался. */
export function peekEntryDeviceChoice(roomCode: string | null | undefined): EntryDeviceChoice | null {
    return pending && roomCode && pending.roomCode === roomCode ? pending : null;
}

export function clearEntryDeviceChoice(): void {
    pending = null;
}

/**
 * Кому выбор применяется. Только тому, кто ведёт: ведущему и соведущему.
 * Предпросмотр на экране входа открывался с включёнными микрофоном и камерой
 * по умолчанию, так что у участника «включено» — чаще умолчание, чем решение
 * (с CORE-2056 камера у него в предпросмотре по умолчанию выключена);
 * а открытый микрофон стоит ему звука практики (CORE-1601). Участник включает
 * устройства сам, как и раньше.
 */
export function entryChoiceApplies(params: { isRoomHost: boolean; isCohost: boolean; isSpectator: boolean }): boolean {
    if (params.isSpectator) return false;
    return params.isRoomHost || params.isCohost;
}

/**
 * CORE-2056 (прод 03.11, комната 1329) — с чем открывается камера в предпросмотре
 * экрана входа, пока человек её не трогал.
 *
 * Участнику комната выбор с экрана входа не применяет (`entryChoiceApplies`), и
 * он входил с выключенной камерой, только что увидев себя в предпросмотре. Теперь
 * предпросмотр обещает то же, что сделает комната: камера по умолчанию выключена у
 * всех, кому выбор не применяется. Ведущему и соведущему — как раньше: прошлый
 * выбор, а без него включена. Нажатие на «Камера» в предпросмотре работает у всех.
 */
export function previewCamOnByDefault(params: { choiceApplies: boolean; savedCamOn: boolean | undefined }): boolean {
    if (!params.choiceApplies) return false;
    return params.savedCamOn ?? true;
}

export type EntryChoiceStep = 'wait' | 'apply' | 'drop';

/**
 * Что делать с выбором на этом рендере.
 *
 * Применять можно, когда сошлось всё сразу: комната загружена, на экране сама
 * комната, вход в канал состоялся с правом публиковать, и право вещать открыто.
 *
 * «На экране сама комната» — отдельное условие. До назначенного начала ведущий
 * сидит в ожидании («Скоро начнётся»), где нет ни кнопки микрофона, ни своей
 * плитки. Канал при этом уже открыт, и применённый там выбор вывел бы человека в
 * эфир без единого признака на экране и без способа это выключить (замер на
 * превью: 6 КБ звука и 88 КБ видео за 2 с из зала ожидания). Выбор ждёт начала —
 * это и есть момент из тикета.
 *
 * Отбрасывается выбор только у того, кому он не положен. У ведущего он ждёт,
 * сколько понадобится: в момент начала право и роль могут на рендер-другой
 * пропасть (перезаход в канал, снимок сцены), и отбросить выбор по такому
 * кадру значит потерять его насовсем — прогон на dev 06.10 так и кончился:
 * эфир начался, а переключения не было вовсе.
 */
export function resolveEntryChoiceStep(params: {
    applies: boolean;
    roomLoaded: boolean;
    hubConnected: boolean;
    isPreShow: boolean;
    rtcConnected: boolean;
    isPublisher: boolean;
    mayBroadcast: boolean;
}): EntryChoiceStep {
    if (!params.roomLoaded) return 'wait';
    if (!params.applies) return params.hubConnected ? 'drop' : 'wait';
    return !params.isPreShow && params.rtcConnected && params.isPublisher && params.mayBroadcast ? 'apply' : 'wait';
}
